import { useEffect, useRef, useState } from "react";

// A number that lands like a fruit machine: each digit is a vertical reel that spins through a few
// rotations and eases to a stop, staggered left-to-right so they settle one after another. `format`
// shapes the string (currency / thousands). Non-digit glyphs (£, commas, .) stay static. Honours
// prefers-reduced-motion by snapping straight to the value.
//
// With a `persistKey`, the last value shown for that key is remembered across unmounts, so the reels
// only spin when the value actually changes (first appearance and real updates) — not every time the
// component remounts, e.g. as the header re-renders on each page navigation.
const SPINS = 5; // full 0–9 rotations before landing
const BASE_MS = 620;
const STEP_MS = 130; // each successive digit spins a little longer, so they stop in sequence
const EASE = "cubic-bezier(0.15, 0.85, 0.2, 1)"; // fast start, strong slow-down

const lastShown = new Map();

const Reel = ({ digit, spinKey, durationMs, animateOnMount }) => {
  // Start either at the top (about to spin down) or already resting on the digit (no spin).
  const [state, setState] = useState(() =>
    animateOnMount ? { offset: 0, animate: false } : { offset: digit, animate: false },
  );
  const rafRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    // First run only spins if asked (a genuinely new value); a remount with an unchanged value
    // sits still. After mount, any spinKey change is a real update, so it always spins.
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (!animateOnMount) return undefined;
    }

    setState({ offset: 0, animate: false });
    const outer = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() =>
        setState({ offset: SPINS * 10 + digit, animate: true }),
      );
    });
    return () => {
      cancelAnimationFrame(outer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digit, spinKey]);

  // When the spin ends, snap to the exact resting offset for this digit. The strip is otherwise left
  // parked at translateY(-(SPINS*10+digit)em) indefinitely; if the browser drops the final animation
  // frame (Safari can, with the header's always-running livePulse invalidating the layer), the reel is
  // left a fraction short and appears low. Snapping to the same glyph forces a clean repaint with no
  // visible motion.
  const handleTransitionEnd = () => setState({ offset: digit, animate: false });

  return (
    <span
      aria-hidden="true"
      className="inline-block overflow-hidden"
      style={{ height: "1em" }}
    >
      <span
        className="flex flex-col"
        onTransitionEnd={handleTransitionEnd}
        style={{
          transform: `translateY(-${state.offset}em)`,
          transition: state.animate ? `transform ${durationMs}ms ${EASE}` : "none",
        }}
      >
        {Array.from({ length: SPINS * 10 + 10 }, (_, i) => (
          <span
            key={i}
            className="flex items-end justify-center leading-none"
            style={{ height: "1em" }}
          >
            {i % 10}
          </span>
        ))}
      </span>
    </span>
  );
};

const RollingNumber = ({
  value = 0,
  format = (n) => Math.round(n).toLocaleString(),
  className,
  persistKey,
}) => {
  const text = format(value ?? 0);
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  // Spin on mount only when this value differs from what the key last showed (or the first time it's
  // seen). Without a key it always animates on mount, as before.
  const previous = persistKey ? lastShown.get(persistKey) : undefined;
  const animateOnMount = previous !== text;

  useEffect(() => {
    if (persistKey) lastShown.set(persistKey, text);
  }, [persistKey, text]);

  if (reduced) {
    return <span className={className}>{text}</span>;
  }

  let digitIndex = -1;
  // The caller's className (which may set display, e.g. `block`) goes on the outer wrapper; the inner
  // element always owns the inline-flex baseline alignment so it can't be overridden.
  return (
    <span className={className} aria-label={text}>
      <span
        className="inline-flex items-end leading-none tabular-nums"
        aria-hidden="true"
      >
        {[...text].map((ch, i) => {
          if (ch < "0" || ch > "9") {
            return <span key={i}>{ch}</span>;
          }
          digitIndex += 1;
          return (
            <Reel
              key={i}
              digit={Number(ch)}
              spinKey={text}
              durationMs={BASE_MS + digitIndex * STEP_MS}
              animateOnMount={animateOnMount}
            />
          );
        })}
      </span>
    </span>
  );
};

export default RollingNumber;
